import React, { useState, useContext } from 'react';
import { Eye, EyeSlash } from 'iconsax-react';
import { ThemeContext } from '../theme';

/**
 * Reusable form input field matching the Taleo design system.
 *
 * Props:
 *   label      — field label text
 *   required   — shows a red asterisk when true (default false)
 *   type       — 'text' | 'email' | 'password' (default 'text')
 *   placeholder
 *   value / onChange — controlled input
 *   readOnly   — makes the input non-editable (for dropdown display)
 *   rightIcon  — any React node rendered as a right-side icon
 *   hint       — optional helper text rendered below the input (right-aligned)
 *   scale      — uniform size multiplier (default 1)
 */
export default function InputField({
  label,
  required = false,
  type = 'text',
  placeholder = '',
  value,
  onChange,
  readOnly = false,
  rightIcon,
  hint,
  scale = 1,
}) {
  const theme = useContext(ThemeContext);
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const resolvedType = isPassword && showPassword ? 'text' : type;

  const s = (n) => Math.round(n * scale);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: s(12), width: '100%' }}>
      <style>{`.taleo-input::placeholder { color: ${theme.placeholderColor}; }`}</style>

      {/* Label */}
      {label && (
        <div
          className="font-satoshi"
          style={{
            fontWeight: 700,
            fontSize: s(18),
            lineHeight: `${s(24)}px`,
            color: theme.text100,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
          {required && <span style={{ color: '#d46c6c' }}>*</span>}
        </div>
      )}

      {/* Input row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: s(12),
          height: s(64),
          borderRadius: s(20),
          border: `1.5px solid ${theme.inputBorder}`,
          backgroundColor: theme.inputBg,
          padding: `0 ${s(24)}px`,
          boxSizing: 'border-box',
          cursor: readOnly ? 'pointer' : 'auto',
        }}
      >
        <input
          type={resolvedType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
          className="font-satoshi taleo-input"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: s(18),
            lineHeight: `${s(28)}px`,
            color: theme.text100,
            fontWeight: 400,
            minWidth: 0,
            cursor: readOnly ? 'pointer' : 'auto',
          }}
        />

        {/* Password toggle */}
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {showPassword
              ? <Eye      size={s(24)} variant="Linear" color={theme.text400} />
              : <EyeSlash size={s(24)} variant="Linear" color={theme.text400} />
            }
          </button>
        )}

        {/* Generic right icon (e.g. chevron for dropdowns) */}
        {!isPassword && rightIcon && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {rightIcon}
          </div>
        )}
      </div>

      {/* Hint */}
      {hint && (
        <div
          className="font-satoshi"
          style={{
            fontSize: s(14),
            lineHeight: `${s(20)}px`,
            color: theme.text400,
            textAlign: 'right',
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
